// Sprint 12 DOMAIN.A2 — domain pack card for the project dashboard.
//
// Shows the project's current pack and lets a PM change it inline.
// Pack changes flow through PATCH /projects/:id/domain-pack which
// writes the new pack id, audit-logs the change, and (per the
// spec's phase-boundary governance) takes effect at next agent
// session start.

import { useState } from 'react';
import { apiFetch } from '../lib/api';

// The web-side pack type: the same three values as currentType below and the
// API's domainPackSchema enum (apps/api/src/routes/projects.ts).
export type DomainPackType = 'generic' | 'healthcare' | 'bfsi';

export const PACK_LABELS: Record<DomainPackType, string> = {
  generic:    'Generic',
  healthcare: 'Healthcare',
  bfsi:       'BFSI',
};

const PACK_OPTIONS: Array<{ value: 'generic' | 'healthcare' | 'bfsi'; label: string; helper: string }> = [
  { value: 'generic',    label: 'Generic',    helper: 'No industry-specific context.' },
  { value: 'healthcare', label: 'Healthcare', helper: 'HIPAA / NABH / WCAG 2.1 AA references.' },
  { value: 'bfsi',       label: 'BFSI',       helper: 'RBI / SEBI cyber-resilience references.' },
];

interface ProjectDomainPackCardProps {
  projectId:   string;
  /** Current pack type stored on `projects.domain_pack_id`. `null` reads as Generic. */
  currentType: 'generic' | 'healthcare' | 'bfsi' | null;
  /** Called with the freshly-applied pack type so the parent can refresh state. */
  onChange?: (next: 'generic' | 'healthcare' | 'bfsi') => void;
}

export default function ProjectDomainPackCard({
  projectId, currentType, onChange,
}: ProjectDomainPackCardProps) {
  const effective = currentType ?? 'generic';
  const [editing, setEditing] = useState(false);
  const [pending, setPending] = useState<'generic' | 'healthcare' | 'bfsi'>(effective);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apply = async () => {
    if (pending === effective) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/projects/${projectId}/domain-pack`, {
        method: 'PATCH',
        body: JSON.stringify({ packType: pending }),
      });
      onChange?.(pending);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change domain pack');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="project-domain-pack-card"
      style={{
        padding: '16px 18px',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        fontFamily: 'var(--font-body)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
          Domain Pack
        </h3>
        {!editing && (
          <button
            data-testid="domain-pack-change-button"
            onClick={() => { setPending(effective); setEditing(true); setError(null); }}
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--primary)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            Change
          </button>
        )}
      </div>

      {!editing && (
        <div data-testid="domain-pack-current" style={{ marginTop: 8, fontSize: 14, color: 'var(--text)' }}>
          <strong>{PACK_LABELS[effective]}</strong>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 8 }}>
            {PACK_OPTIONS.find(o => o.value === effective)?.helper}
          </span>
        </div>
      )}

      {editing && (
        <div data-testid="domain-pack-editor" style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {PACK_OPTIONS.map(opt => (
            <label
              key={opt.value}
              data-testid={`domain-pack-option-${opt.value}`}
              style={{
                display: 'flex',
                gap: 8,
                padding: '8px 10px',
                border: `1px solid ${pending === opt.value ? 'var(--primary)' : 'var(--border)'}`,
                borderRadius: 'var(--radius-sm)',
                cursor: busy ? 'default' : 'pointer',
                background: pending === opt.value ? 'rgba(124,58,237,0.06)' : 'transparent',
              }}
            >
              <input
                type="radio"
                name="domain-pack"
                value={opt.value}
                checked={pending === opt.value}
                onChange={() => setPending(opt.value)}
                disabled={busy}
                style={{ marginTop: 3 }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{opt.label}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{opt.helper}</div>
              </div>
            </label>
          ))}

          <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
            Pack change takes effect at the next agent session — in-flight sessions keep their current pack.
          </p>

          {error && (
            <p data-testid="domain-pack-error" style={{ fontSize: 12, color: 'var(--error)' }}>{error}</p>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button
              data-testid="domain-pack-apply"
              onClick={apply}
              disabled={busy}
              style={{
                padding: '6px 14px',
                background: 'var(--primary)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                fontSize: 12,
                fontWeight: 600,
                cursor: busy ? 'default' : 'pointer',
                opacity: busy ? 0.6 : 1,
              }}
            >
              {busy ? 'Applying…' : 'Apply'}
            </button>
            <button
              data-testid="domain-pack-cancel"
              onClick={() => { setEditing(false); setError(null); }}
              disabled={busy}
              style={{
                padding: '6px 14px',
                background: 'transparent',
                color: 'var(--text-secondary)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 12,
                cursor: busy ? 'default' : 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
